/**
* Firebase Crashlytics is straightforward to integrate, especially since you already have Firebase set up. 
* The integration complexity depends on whether you're building for web-only or also targeting native 
* platforms (iOS/Android via Capacitor).
* Would You Still Need GlobalErrorHandlerService?
* Yes, but it would serve a complementary role. Here's why:
*
* What Crashlytics Provides:
* ✅ Automatic crash reporting for native apps (iOS/Android)
* ✅ Real-time crash analytics and trends
* ✅ Stack traces with source maps
* ✅ Device and OS information
* ✅ Breadcrumbs (user actions before crash)
* ✅ Free and integrated with Firebase Console
* 
* What GlobalErrorHandlerService Provides:
* ✅ Custom business logic (like preventing duplicate errors)
* ✅ User-friendly error messages via toast
* ✅ Integration with LogRocket for session replay
* ✅ Custom error metadata (account number, user info, connectivity status)
* ✅ Firestore logging for custom queries/reports
*
* The Ideal Setup:
* Keep GlobalErrorHandlerService AND add Crashlytics - they complement each other perfectly!
* When I'm ready, uncomment fc-ptb
*/
/* eslint-disable no-console */
import { Injectable, ErrorHandler, Injector } from '@angular/core';
import { Platform } from '@ionic/angular';
import * as logRocket from 'logrocket';
// fc-ptb import { FirebaseCrashlytics } from '@capacitor-firebase/crashlytics';

import { FirestorePathsService } from './firestorePaths.service';
import { ConnectivityService } from '../lib/connectivity.service';
import { DateService } from './date.service';
import { FirestoreService } from '../lib/firestore.service';
import { SessionService } from '../lib/session.service';
import { UxHelperService } from '../lib/uxHelper.service';

import type { TError } from '@shared/models';

@Injectable({
    'providedIn': 'root'
})
export class GlobalErrorHandlerService implements ErrorHandler {
    private lastCustomError!: TError;
    private crashlyticsEnabled = false;

    constructor(
        private injector: Injector,
        private platform: Platform,
        private firestorePathsService: FirestorePathsService, 
        private firestoreService: FirestoreService,
        private sessionService: SessionService, 
        private connectivityService: ConnectivityService, 
        private dateService: DateService
    ) {
        // this.initializeCrashlytics();
    }

    // private async initializeCrashlytics(): Promise<void> {
    //     // Only enable Crashlytics on native platforms
    //     if (this.platform.is('capacitor')) {
    //         try {
    //             // Enable Crashlytics
    //             // fc-ptb await FirebaseCrashlytics.setEnabled({ enabled: true });
    //             this.crashlyticsEnabled = true;
    //             console.log('Crashlytics enabled');
                
    //             // Set user identifier
    //             const user = this.sessionService?.getCurrentUser();
    //             if (user?.email) {
    //                 // fc-ptb                    await FirebaseCrashlytics.setUserId({ userId: user.email });
    //             }
                
    //             // Set custom keys for debugging
    //             const accountNumber = this.sessionService?.getAccountNumber();
    //             if (accountNumber) {
    //                 // fc-ptb                    await FirebaseCrashlytics.setCustomKey({ 
    //                 // fc-ptb                        key: 'accountNumber', 
    //                 // fc-ptb                        value: accountNumber 
    //                 // fc-ptb                    });
    //             }
    //         } catch (error) {
    //             console.warn('Failed to initialize Crashlytics:', error);
    //         }
    //     }
    // }

    public async handleError(error: any) {
        let errMsg = '';
        let stack = '';
        let fileName = 'Unknown';
        let lineNumber = 'Unknown';
        let columnNumber = 'Unknown';

        // Handle different error types
        if (typeof error === 'string') {
            errMsg = error;
        } else if (error instanceof Error) {
            errMsg = error.message;
            stack = error.stack || '';
            
            // Parse stack trace for location info
            const location = this.parseStackTrace(stack);
            fileName = location.fileName;
            lineNumber = location.lineNumber;
            columnNumber = location.columnNumber;
        } else if (error.rejection) {
            // Promise rejection
            if (error.rejection instanceof Error) {
                errMsg = error.rejection.message;
                stack = error.rejection.stack || '';
                
                const location = this.parseStackTrace(stack);
                fileName = location.fileName;
                lineNumber = location.lineNumber;
                columnNumber = location.columnNumber;
            }
        }

        // Native Error properties (these may be undefined in production builds)
        if (error.fileName) {
            fileName = error.fileName;
        }
        if (error.lineNumber) {
            lineNumber = String(error.lineNumber);
        }
        if (error.columnNumber) {
            columnNumber = String(error.columnNumber);
        }

        const errorDate = this.dateService.formatTimestamp('now');
        const user = this.sessionService?.getCurrentUser();
        
        const customError: TError = {
            'errDate': errorDate,
            'accountNumber': this.sessionService?.getAccountNumber() || 'Unknown',
            'usersFullName': this.sessionService?.getUsersFullName || 'Unknown',
            'usersEmail': user?.email || 'Unknown',
            'internetConnectivityStatus': this.connectivityService.isOnlineStr(),
            'errorMessage': errMsg,
            'stack': stack,
            'fileName': fileName,
            'lineNumber': lineNumber,
            'columnNumber': columnNumber
        };

        // Log this error to all services
        await this.logError(error, customError);
        console.error(customError);

        const msg = 'An unknown error occurred. The developers have been alerted.';
        try {
            // Resolve lazily to avoid eager Ionic delegate/ApplicationRef wiring during bootstrap.
            const uxHelperService = this.injector.get(UxHelperService);
            await uxHelperService.showToastWithCloseButton(msg);
        } catch (toastError) {
            console.warn('Failed to show error toast:', toastError);
        }
    }

    private parseStackTrace(stack: string): { 
        fileName: string; 
        lineNumber: string; 
        columnNumber: string 
    } {
        if (!stack) {
            return { 
                fileName: 'Unknown', 
                lineNumber: 'Unknown', 
                columnNumber: 'Unknown' 
            };
        }

        const lines = stack.split('\n');
        
        // Find the first meaningful stack frame (skip error message line)
        for (let i = 1; i < lines.length; i++) {
            const line = lines[i].trim();
            
            // Chrome/Edge format: "at functionName (http://localhost:4200/main.js:123:45)"
            let match = line.match(/\(([^)]+):(\d+):(\d+)\)/);
            
            if (!match) {
                // Alternate Chrome format: "at http://localhost:4200/main.js:123:45"
                match = line.match(/at\s+([^:]+):(\d+):(\d+)/);
            }
            
            if (!match) {
                // Firefox format: "functionName@http://localhost:4200/main.js:123:45"
                match = line.match(/@([^:]+):(\d+):(\d+)/);
            }
            
            if (match) {
                return {
                    fileName: this.cleanFileName(match[1]),
                    lineNumber: match[2],
                    columnNumber: match[3]
                };
            }
        }
        
        return { 
            fileName: 'Unknown', 
            lineNumber: 'Unknown', 
            columnNumber: 'Unknown' 
        };
    }

    private cleanFileName(filePath: string): string {
        // Remove query parameters
        filePath = filePath.split('?')[0];
        
        // Extract just the filename from full path
        const parts = filePath.split('/');
        const fileName = parts[parts.length - 1];
        
        // Remove leading/trailing whitespace
        return fileName.trim();
    }

    private async logError(error: any, customError: TError): Promise<void> {
        // Do not repeatedly log the same error more than once.
        if (this.lastCustomError && customError.errorMessage === this.lastCustomError.errorMessage) {
            return;
        }
        this.lastCustomError = customError;

        // Log to Crashlytics (native apps only)
        if (this.crashlyticsEnabled) {
            /* fc-ptb
            try {
                // Set custom keys for this crash

                await FirebaseCrashlytics.setCustomKey({ 
                    key: 'accountNumber', 
                    value: customError.accountNumber 
                });
                await FirebaseCrashlytics.setCustomKey({ 
                    key: 'userEmail', 
                    value: customError.usersEmail 
                });
                await FirebaseCrashlytics.setCustomKey({ 
                    key: 'connectivity', 
                    value: customError.internetConnectivityStatus 
                });
                
                // Log the error message
                await FirebaseCrashlytics.log({ message: customError.errorMessage || 'Unknown error' });
                
                // Record the exception
                if (error instanceof Error) {
                    await FirebaseCrashlytics.recordException({ 
                        message: error.message,
                        stacktrace: error.stack || ''
                    });
                } else {
                    await FirebaseCrashlytics.recordException({ 
                        message: String(error),
                        stacktrace: customError.stack || ''
                    });
                }
                
                console.log('Error logged to Crashlytics');

            } catch (crashlyticsError) {
                console.warn('Failed to log to Crashlytics:', crashlyticsError);
            }
            */
        }

        // Log this error to Firestore (all platforms)
        try {
            await this.firestoreService.add(this.firestorePathsService.getErrorsPath(), customError);
        } catch (firestoreError) {
            console.warn('Failed to log to Firestore:', firestoreError);
        }

        // Log this error to LogRocket (web platform)
        try {
            logRocket.captureException(error, {
                tags: {
                    subscription: 'Pro',
                    platform: this.platform.platforms().join(',')
                },
                extra: {
                    customError: JSON.stringify(customError)
                }
            });
        } catch (logRocketError) {
            console.warn('Failed to log to LogRocket:', logRocketError);
        }
    }

    /**
     * Call this when user logs in to associate crashes with user
     */
    public async setUserIdentifier(userId: string, email: string): Promise<void> {
        if (this.crashlyticsEnabled) {
            try {
                // fc-ptb await FirebaseCrashlytics.setUserId({ userId: email });
                // fc-ptb await FirebaseCrashlytics.setCustomKey({ key: 'userId', value: userId });
                console.log('Crashlytics user identifier set');
            } catch (error) {
                console.warn('Failed to set Crashlytics user identifier:', error);
            }
        }
    }

    /**
     * Add breadcrumb for debugging crash context
     */
    public async logBreadcrumb(message: string): Promise<void> {
        if (this.crashlyticsEnabled) {
            try {
                // fc-ptb await FirebaseCrashlytics.log({ message });
            } catch (error) {
                console.warn('Failed to log breadcrumb:', error);
            }
        }
    }
}